import {
  type TransactionFilters,
  convertHeadersToArray,
  filterTransactions,
  getContentType,
  getContentTypeCategory,
  transactionsCountLimit,
} from '@carboncopyco/editor-utils';
import { PlusOutlineIcon } from '@carboncopyco/icons';
import { GreenMagnifyingGlassIllustration } from '@carboncopyco/illustrations';
import { Button, TextBase, TooltipBubble } from '@carboncopyco/ui';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { SearchBoxInput } from '../../../SearchBoxInput';
import {
  CancelButton,
  FormLayout,
  SubmitButton,
} from '../../../TransactionRuleModal';
import { useSearch } from '../../../hooks';
import { useCreateTransactionRuleContext } from '../../CreateTransactionRuleContext';
import type { Transaction } from '../../types';
import { TransactionList } from './TransactionList';
import { TransactionSearchFilters } from './TransactionSearchFilters';

const maxSearchChars = 100;

export const SelectTransactionStep = () => {
  const {
    transactions,
    onSelectTransaction,
    cancelCreation,
    nextStep,
    onResponseTypeSelected,
    isVisualSearch,
    cancelVisualSearch,
  } = useCreateTransactionRuleContext();
  const { register, handleSubmit, setValue, watch } = useForm();
  const [filters, setFilters] = useState<TransactionFilters>({});

  const searchOptions = useMemo(
    () => ({
      searchKeys: ['original.request.url', 'original.response.body'],
      dataSet: transactions,
      options: {
        useExtendedSearch: true,
        includeScore: true,
        ignoreLocation: true,
        threshold: 0.0,
        maxLength: maxSearchChars,
      },
    }),
    [transactions]
  );

  const {
    results: searchedTransactions,
    setSearchValue,
    searchValue,
  } = useSearch<Transaction>(searchOptions);

  const filteredTransactions = useMemo(
    () => filterTransactions(searchedTransactions, filters),
    [searchedTransactions, filters]
  );

  const selectedTransaction = watch('selectedTransaction');

  const submitForm = ({
    selectedTransaction,
  }: {
    selectedTransaction: Transaction;
  }) => {
    const headers = convertHeadersToArray(
      selectedTransaction?.original.response.headers
    );

    const contentTypeCategory = getContentTypeCategory(
      getContentType(headers ?? [])
    );
    onResponseTypeSelected(['static', contentTypeCategory]);
    onSelectTransaction(selectedTransaction);

    nextStep();
  };

  return (
    <FormLayout onSubmit={handleSubmit(submitForm)}>
      <FormLayout.Content>
        <div className="h-full flex-col flex gap-2.5 pb-2.5">
          <div className="flex flex-col flex-1 pt-5 gap-4 overflow-hidden">
            <div className="flex gap-10 items-start">
              <div className="flex-1">
                {isVisualSearch ? (
                  <div className="flex gap-1 items-center w-full">
                    <div className="flex-1">
                      <SearchBoxInput
                        value="Visual search results for selected element"
                        disabled
                      />
                    </div>
                    <TooltipBubble
                      placement="top"
                      title="Switch to text search"
                      appendTo="parent"
                    >
                      <Button
                        label="T"
                        small
                        secondary
                        onClick={cancelVisualSearch}
                      />
                    </TooltipBubble>
                  </div>
                ) : (
                  <SearchBoxInput
                    autoFocus
                    value={searchValue}
                    onChange={setSearchValue}
                    placeholder="Search"
                    maxLength={maxSearchChars}
                  />
                )}
              </div>
              <TransactionSearchFilters onChange={setFilters} />
            </div>
            {filteredTransactions.length ? (
              <>
                <input
                  {...register('selectedTransaction')}
                  hidden
                />
                <TransactionList
                  transactions={filteredTransactions}
                  showLimitMessage={
                    transactions.length >= transactionsCountLimit
                  }
                  searchValue={searchValue}
                  selectedTransaction={selectedTransaction}
                  onSelectTransaction={(transaction) => {
                    setValue('selectedTransaction', transaction);
                  }}
                />
              </>
            ) : (
              <div className="flex-1 flex flex-col justify-center items-center gap-5">
                <GreenMagnifyingGlassIllustration
                  height={58}
                  width={58}
                />
                <TextBase
                  label="No matching requests"
                  color="text-ds-natural-600"
                />
              </div>
            )}
          </div>

          <div
            onClick={() => {
              onSelectTransaction(undefined);
              onResponseTypeSelected(['static', null]);
              nextStep();
            }}
            className="flex w-full p-2.5 rounded-md outline-dashed outline-1 outline-ds-natural-150 cursor-pointer"
          >
            <Button
              link
              blue
              leftIcon={<PlusOutlineIcon size={20} />}
              label="Define selector from scratch"
              noPadding
            />
          </div>
        </div>
      </FormLayout.Content>
      <FormLayout.Footer>
        <div className="flex-1" />
        <CancelButton onClick={cancelCreation} />
        <SubmitButton
          label="Next"
          disabled={!selectedTransaction}
        />
      </FormLayout.Footer>
    </FormLayout>
  );
};
